import {
  Timestamp,
  addDoc,
  collection,
  deleteDoc,
  deleteField,
  doc,
  getCountFromServer,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import { useEffect, useState } from 'react'
import type {
  EventDoc,
  EventSource,
  EventFuel,
  SeriesDoc,
  SessionDoc,
  SessionType,
  TimingVendor,
} from '@racedash/shared'
import { db } from '../lib/firebase'

export interface Event extends EventDoc {
  id: string
}
export interface Series extends SeriesDoc {
  id: string
}
export interface Session extends SessionDoc {
  id: string
}

/**
 * The coalesced calendar: every event, whatever vendor it came from.
 *
 * Deliberately one list rather than one per vendor. Vendors differ in how
 * events are *discovered* — scraped for TSL, entered by hand for RIS — but once
 * catalogued they are the same kind of thing, and a weekend's racing does not
 * sort itself by timing supplier.
 */
export function useEvents(vendor?: TimingVendor) {
  const [events, setEvents] = useState<Event[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'events'), orderBy('startsAt', 'desc')),
      (snap) => {
        const all = snap.docs.map((d) => ({ id: d.id, ...(d.data() as EventDoc) }))
        setEvents(vendor ? all.filter((e) => e.source?.vendor === vendor) : all)
      },
      (e) => setError(e.message),
    )
  }, [vendor])

  return { events, error }
}

export function useSeries() {
  const [series, setSeries] = useState<Series[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return onSnapshot(
      query(collection(db, 'series'), orderBy('name')),
      (snap) => setSeries(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SeriesDoc) }))),
      // Never collapse a failure into an empty list. An empty series list is a
      // meaningful state that the UI explains ("add one before adding
      // sessions"), so reporting a broken query as emptiness sends the reader
      // off to fix the wrong thing.
      (e) => setError(e.message),
    )
  }, [])

  return { series, error }
}

/**
 * One event's timetable, in running order.
 *
 * Needs the composite index on (eventId, scheduledStart) declared in
 * firestore.indexes.json. Without it this listener fails rather than returning
 * nothing, which is why the failure is reported instead of swallowed.
 */
export function useSessions(eventId: string | undefined) {
  const [sessions, setSessions] = useState<Session[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!eventId) {
      setSessions(null)
      return
    }
    return onSnapshot(
      query(
        collection(db, 'sessions'),
        where('eventId', '==', eventId),
        orderBy('scheduledStart'),
      ),
      (snap) => setSessions(snap.docs.map((d) => ({ id: d.id, ...(d.data() as SessionDoc) }))),
      (e) => setError(e.message),
    )
  }, [eventId])

  return { sessions, error }
}

export interface SeriesInput {
  name: string
  /** Matching happens on these rather than on the display name, because the
   *  vendor's string is what actually arrives on the wire. */
  vendorNames: Partial<Record<TimingVendor, string[]>>
}

export async function createSeries(input: SeriesInput) {
  return addDoc(collection(db, 'series'), { ...input, createdAt: serverTimestamp() })
}

/**
 * Edits a series in place.
 *
 * setDoc with merge rather than updateDoc, because removing the last alias for
 * a vendor has to delete that key — an updateDoc of the whole map does replace
 * it, but merge:false on a nested map is easy to get wrong, so the map is
 * written whole and deliberately.
 */
export async function updateSeries(id: string, input: SeriesInput) {
  return setDoc(
    doc(db, 'series', id),
    { ...input, updatedAt: serverTimestamp() },
    { mergeFields: ['name', 'vendorNames', 'updatedAt'] },
  )
}

/**
 * How many sessions name this series.
 *
 * A count rather than the documents: the answer is only ever "is this safe to
 * delete, and if not how badly", and reading a season of sessions to learn a
 * number is a lot of documents for one integer.
 */
export async function countSessionsForSeries(seriesId: string): Promise<number> {
  const snap = await getCountFromServer(
    query(collection(db, 'sessions'), where('seriesId', '==', seriesId)),
  )
  return snap.data().count
}

/**
 * Deletes a series.
 *
 * The caller checks for references first. That check is a guard against a
 * mistake, not an enforced constraint — Firestore rules cannot ask whether a
 * query returns nothing, so nothing stops a determined admin or a session
 * created in the moment between the count and the delete. It catches the case
 * that actually happens, which is somebody not realising.
 */
export async function deleteSeries(id: string) {
  return deleteDoc(doc(db, 'series', id))
}

export interface EventInput {
  name: string
  trackId: string
  timezone: string
  startsAt: Date
  endsAt: Date
  source: EventSource
}

export async function createEvent(input: EventInput) {
  return addDoc(collection(db, 'events'), {
    ...input,
    startsAt: Timestamp.fromDate(input.startsAt),
    endsAt: Timestamp.fromDate(input.endsAt),
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  })
}

export async function updateEvent(id: string, input: EventInput) {
  return updateDoc(doc(db, 'events', id), {
    ...input,
    startsAt: Timestamp.fromDate(input.startsAt),
    endsAt: Timestamp.fromDate(input.endsAt),
    updatedAt: serverTimestamp(),
  })
}

/**
 * Deleting an event leaves its sessions orphaned, so remove them first. There
 * is no cascade in Firestore, and a stranded session would still be matched
 * against by anything scanning the schedule.
 */
export async function deleteEvent(id: string) {
  const sessions = await getDocs(query(collection(db, 'sessions'), where('eventId', '==', id)))
  await Promise.all(sessions.docs.map((d) => deleteDoc(d.ref)))
  await deleteDoc(doc(db, 'events', id))
}

export interface SessionInput {
  eventId: string
  seriesId: string
  name: string
  type: SessionType
  scheduledStart: Date
  scheduledEnd: Date
}

export async function createSession(input: SessionInput) {
  return addDoc(collection(db, 'sessions'), {
    ...input,
    scheduledStart: Timestamp.fromDate(input.scheduledStart),
    scheduledEnd: Timestamp.fromDate(input.scheduledEnd),
  })
}

/** Edits a session in place. The id is stable, so anything already matched
 *  against this session keeps pointing at it. */
export async function updateSession(id: string, input: SessionInput) {
  return updateDoc(doc(db, 'sessions', id), {
    ...input,
    scheduledStart: Timestamp.fromDate(input.scheduledStart),
    scheduledEnd: Timestamp.fromDate(input.scheduledEnd),
  })
}

export async function deleteSession(id: string) {
  return deleteDoc(doc(db, 'sessions', id))
}

/**
 * Sets where the event's fuel is reported, or clears it. The reconciler starts
 * a fuel job alongside capture for any event with one, so this is the switch.
 */
export async function setEventFuel(id: string, fuel: EventFuel | null) {
  return updateDoc(doc(db, 'events', id), {
    fuel: fuel ?? deleteField(),
    updatedAt: serverTimestamp(),
  })
}

/** Sets the RIS addressing once somebody has found it — the readiness gate
 *  that turns a catalogued event into a capturable one. */
export async function setEventSource(id: string, source: EventSource) {
  return setDoc(
    doc(db, 'events', id),
    { source, updatedAt: serverTimestamp() },
    { merge: true },
  )
}
